import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Countdown from 'react-countdown';
import { giveAnsToPoll } from '../../../service/feed.service';
import palette from '../../../theme/palette';
import Iconify from '../../../fl-components/Iconify';
import { UPDATE_FEED } from '../../../redux/constants';

export default function PollOptions({ post }) {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedOpId, setSelectedOpId] = useState('');
  const [timeUp, setTimeUp] = useState(moment().isAfter(post.endTime));

  const isAnsGivenOrPollEnded = useMemo(() => {
    // If time is up
    if (moment().isAfter(post.endTime)) {
      return true;
    }

    // If user has given ans
    if (post?.pollOptions.filter((op) => op.interactedUsers.includes(currentUser.id)).length > 0) {
      return true;
    }

    return false;
  }, [post, timeUp]);

  const voteCountArray = useMemo(() => {
    const array = [];
    post?.pollOptions.forEach((op) => {
      array.push(op.interactionPercentage);
    });
    return array;
  }, [post]);

  const updateAns = async (optionId) => {
    setLoading(true);
    setSelectedOpId(optionId);
    // do api call
    const res = await giveAnsToPoll(post?.id ?? post?._id, currentUser.id, optionId);
    if (res) {
      dispatch({
        type: UPDATE_FEED,
        payload: {
          isUpdateOrg: true,
          feedObj: res
        }
      });
      setLoading(false);
      setSelectedOpId('');
    } else {
      setLoading(false);
      setSelectedOpId('');
    }
  };

  return (
    <>
      {post.pollOptions.map((po) => (
        <Box
          key={po._id}
          sx={{
            textAlign: isAnsGivenOrPollEnded ? 'unset' : 'center',
            p: 1,
            fontWeight: 'bold',
            marginTop: '8px !important',
            position: 'relative',
            ...(!isAnsGivenOrPollEnded && {
              border: `1px solid ${palette.grey[300]}`,
              borderRadius: 4,
              '&:hover': {
                cursor: 'pointer',
                background: palette.grey[200]
              }
            }),
            ...(isAnsGivenOrPollEnded && {
              display: 'flex',
              justifyContent: 'space-between'
            })
          }}
          onClick={isAnsGivenOrPollEnded || loading ? () => {} : () => updateAns(po._id)}
        >
          <Typography
            sx={{
              zIndex: 1,
              alignItems: 'center',
              display: 'flex',
              fontWeight: isAnsGivenOrPollEnded ? 'normal' : 'bold',
              ...(Math.max(...voteCountArray) === po.interactionPercentage &&
                po.interactionPercentage !== 0 && {
                  fontWeight: 'bold'
                }),
              ...(!isAnsGivenOrPollEnded && {
                justifyContent: 'center'
              }),
              ...(isAnsGivenOrPollEnded && {
                alignItems: 'center'
              }),
              ...(Math.max(...voteCountArray) === po.interactionPercentage &&
                po.interactionPercentage !== 0 &&
                moment().isAfter(post.endTime) && {
                  color: '#036d08'
                })
            }}
          >
            {po.value}
            {loading && selectedOpId === po._id && (
              <CircularProgress color="inherit" size={15} sx={{ ml: 1 }} />
            )}
            {po.interactedUsers.includes(currentUser.id) && (
              <Tooltip title="You opted this option" arrow placement="top">
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Iconify icon="charm:tick" sx={{ marginLeft: '5px' }} />
                </span>
              </Tooltip>
            )}
          </Typography>
          {isAnsGivenOrPollEnded && (
            <Typography
              sx={{
                zIndex: 1,
                fontWeight: isAnsGivenOrPollEnded ? 'normal' : 'bold',
                ...(Math.max(...voteCountArray) === po.interactionPercentage &&
                  po.interactionPercentage !== 0 && {
                    fontWeight: 'bold',
                    ...(moment().isAfter(post.endTime) && {
                      color: '#036d08'
                    })
                  })
              }}
            >
              {po.interactionPercentage}%
            </Typography>
          )}
          {(po.interactedUsers.includes(currentUser.id) || isAnsGivenOrPollEnded) && (
            <Box
              sx={{
                animation: 'anim 1s ease',
                '@keyframes anim': {
                  '0%': {
                    width: 0
                  },
                  '100%': {
                    transform: `scaleX(100%)`
                  }
                },
                width: po.interactionPercentage === 0 ? '7px' : `${po.interactionPercentage}%`,
                backgroundColor:
                  Math.max(...voteCountArray) === po.interactionPercentage &&
                  po.interactionPercentage !== 0 &&
                  moment().isAfter(post.endTime)
                    ? '#1df02636'
                    : 'rgb(207, 217, 222)',
                ...(isAnsGivenOrPollEnded
                  ? {
                      borderRadius: '8px'
                    }
                  : {
                      borderTopLeftRadius: '8px',
                      borderBottomLeftRadius: '8px'
                    }),
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0
              }}
            />
          )}
        </Box>
      ))}

      <Stack direction="row" spacing={0.3} alignItems="center">
        <Typography>{post?.totalVotes ?? 0} votes</Typography>
        <Iconify icon="bi:dot" />
        <Typography>
          {moment().isAfter(post.endTime) ? (
            'Final Result'
          ) : (
            <Countdown
              date={moment(post.endTime).format()}
              renderer={(props) => {
                if (props?.days > 0) return `${props?.days} days left`;
                if (props?.hours > 0) return `${props?.hours} hours left`;
                if (props?.minutes > 0) return `${props?.minutes} minute left`;
                return `${props?.seconds} second left`;
              }}
              onComplete={() => setTimeUp(true)}
            />
          )}
        </Typography>
      </Stack>
    </>
  );
}

PollOptions.propTypes = {
  post: PropTypes.object,
  minutes: PropTypes.number,
  days: PropTypes.number,
  hours: PropTypes.number,
  seconds: PropTypes.number
};
